import { useEffect, useState } from 'preact/hooks';

// The cockpit's OWN width thresholds — the spec (S1) names them, and they
// are deliberately not useBreakpoint's TABLET_MIN/DESKTOP_MIN: that hook
// decides nav chrome (bottom bar vs side rail); this one decides whether
// atlas-cockpit is a column or a bottom bar+sheet, which the spec splits at
// different numbers (900, not 768; 1280, not 1120).
//
// Below 900: no column — a "Talk to Atlas" bar.
// 900–1279: spec leaves this range to atlas-core's judgement (its own
// words). Call: a narrower 320px column rather than the full 360px, so nothing
// crowds the content before 1280 — a single named constant, so the number
// can move without touching any component that reads it.
// >=1280: the full 360px column.
export const COCKPIT_BAR_MAX = 900; // < this width: bar, not column
export const COCKPIT_WIDE_MIN = 1280; // >= this width: the 360px column

export type CockpitWidth = 'bar' | 'narrow' | 'wide';

export function cockpitWidthFor(width: number): CockpitWidth {
  if (width >= COCKPIT_WIDE_MIN) return 'wide';
  if (width >= COCKPIT_BAR_MAX) return 'narrow';
  return 'bar';
}

export const COCKPIT_COLUMN_PX: Record<'narrow' | 'wide', number> = {
  narrow: 320,
  wide: 360,
};

/** Plain window.innerWidth + resize, same pattern as useBreakpoint (kept
 * stubbable by a test: force window.innerWidth then dispatch 'resize'). */
export function useCockpitWidth(): CockpitWidth {
  const [width, setWidth] = useState<CockpitWidth>(() =>
    cockpitWidthFor(typeof window !== 'undefined' ? window.innerWidth : 0),
  );

  useEffect(() => {
    function onResize() {
      setWidth(cockpitWidthFor(window.innerWidth));
    }
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return width;
}
